import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './sidebar.js';
import { Topbar } from './topbar.js';
import { RouteErrorBoundary } from './error-boundary.js';
import { useApiQuery, useVisiblePolling } from '../api/hooks.js';
import type { ApiClientOptions, ApiResult } from '../api/client.js';
import {
  fetchOverviewAlarms,
  type OverviewAlarm,
  type OverviewAlarmsResponse,
} from '../api/overview.js';
import { useCan } from '../session/can.js';
import { describeProblem } from '../i18n/overview-problems.js';
import { ago } from '../i18n/activity-kinds.js';
import { providerLabel, providerLabels } from '../providers/catalog.js';
import { Icon } from '../ui/index.js';

/** Global alarm banner poll cadence (reads the cached evaluator state only). */
export const ALARM_POLL_MS = 60_000;

/**
 * 2026-09-22 — the shell polls GET /admin/overview/alarms once for every page
 * and shares the result: the banner above <Outlet/>, the topbar bell count and
 * the overview's "alarm servisi" tile all read the same state.
 */
export interface GlobalAlarmsState {
  /**
   * Last successful read. `null` = unknown (no capability, endpoint
   * missing/denied on the first read, or not loaded yet).
   */
  readonly data: OverviewAlarmsResponse | null;
  /**
   * A later poll failed (network, timeout, 5xx, 403/404 after a rollback…):
   * `data` is the last successful read, NOT the current state. Never silently
   * stale — every surface marks it.
   */
  readonly stale: boolean;
  /** Client time (ISO) of the last successful read. */
  readonly lastOkAt: string | null;
}

export const UNKNOWN_ALARMS: GlobalAlarmsState = { data: null, stale: false, lastOkAt: null };

const OverviewAlarmsContext = createContext<GlobalAlarmsState>(UNKNOWN_ALARMS);
export const OverviewAlarmsProvider = OverviewAlarmsContext.Provider;

export function useOverviewAlarms(): GlobalAlarmsState {
  return useContext(OverviewAlarmsContext);
}

const ALARMS_DISABLED: ApiResult<OverviewAlarmsResponse> = {
  kind: 'error',
  status: 0,
  reason: 'alarms_disabled',
  body: null,
};

function useGlobalAlarms(enabled: boolean): GlobalAlarmsState {
  const [lastOkAt, setLastOkAt] = useState<string | null>(null);
  const runner = useCallback(
    async (o: ApiClientOptions): Promise<ApiResult<OverviewAlarmsResponse>> => {
      if (!enabled) return ALARMS_DISABLED;
      const res = await fetchOverviewAlarms(o);
      if (res.kind === 'ok' && !o.signal?.aborted) setLastOkAt(new Date().toISOString());
      return res;
    },
    [enabled],
  );
  const query = useApiQuery<OverviewAlarmsResponse>(runner, [enabled]);
  useVisiblePolling(query.reload, ALARM_POLL_MS, enabled);
  // A FIRST-read 401/403/404/network/malformed lands in `error` (or
  // auth_required): the banner simply stays hidden — an optional endpoint must
  // never break a page. After a success, useApiQuery keeps the old data and
  // only sets `refreshError`; that is surfaced as `stale`, never hidden.
  const data = enabled && query.kind === 'ok' ? query.data : null;
  const stale = data !== null && query.refreshError !== null;
  const okAt = data !== null ? lastOkAt : null;
  return useMemo(() => ({ data, stale, lastOkAt: okAt }), [data, stale, okAt]);
}

/** Bell count: active alarms, plus one when the evaluator itself is silent. */
export function activeAlarmCount(alarms: OverviewAlarmsResponse | null): number | null {
  if (!alarms) return null;
  return alarms.alarms.length + (alarms.evaluatorAlive ? 0 : 1);
}

/**
 * Red when any alarm is critical or the evaluator is silent — the server
 * emits `evaluator_silent` as critical, so banner, bell and the problems
 * strip agree.
 */
export function alarmsCritical(alarms: OverviewAlarmsResponse | null): boolean {
  if (!alarms) return false;
  return !alarms.evaluatorAlive || alarms.alarms.some((a) => a.severity === 'critical');
}

/** One banner line: Turkish title, the providers it concerns, and its age. */
export function alarmLine(alarm: OverviewAlarm): string {
  const ids = alarm.providerIds;
  const single = ids.length === 1 ? ids[0] ?? null : null;
  const text = describeProblem(alarm.code, alarm.params, single);
  // Provider-scoped codes already prefix the title with the label.
  const titled = single !== null && text.title.includes(providerLabel(single));
  const scope = ids.length > 0 && !titled ? ` — ${providerLabels(ids)}` : '';
  const since = alarm.since ? ` · ${ago(alarm.since)} başladı` : '';
  return `${text.title}${scope}${since}`;
}

export interface AlarmBannerProps {
  readonly alarms: OverviewAlarmsResponse | null;
  /** The latest alarms poll failed: `alarms` is the last successful read. */
  readonly stale?: boolean;
  /** Client time (ISO) of the last successful read (for the stale line). */
  readonly lastOkAt?: string | null;
  /** Hidden on the overview itself (the problems strip is already there). */
  readonly showOverviewLink: boolean;
}

const MAX_BANNER_LINES = 3;

export function AlarmBanner({
  alarms,
  stale = false,
  lastOkAt = null,
  showOverviewLink,
}: AlarmBannerProps): JSX.Element | null {
  if (!alarms) return null;
  const critical = alarms.alarms.filter((a) => a.severity === 'critical');
  const warn = alarms.alarms.filter((a) => a.severity !== 'critical');
  const evaluatorDead = !alarms.evaluatorAlive;
  // A failed refresh after an all-clear must not read as "all clear".
  if (critical.length === 0 && warn.length === 0 && !evaluatorDead && !stale) return null;

  // evaluator_silent is critical on the server too (problems strip + bell agree).
  const tone = critical.length > 0 || evaluatorDead ? 'critical' : 'warn';
  const ordered = [...critical, ...warn];
  const shown = ordered.slice(0, MAX_BANNER_LINES);
  const more = ordered.length - shown.length;
  const heading =
    critical.length > 0
      ? `${critical.length} kritik alarm${warn.length > 0 ? ` · ${warn.length} uyarı` : ''}`
      : warn.length > 0
        ? `${warn.length} uyarı`
        : evaluatorDead
          ? 'Alarm servisi yanıt vermiyor'
          : 'Alarm durumu okunamadı';
  const lastEval = alarms.evaluatedAt ? ` (son değerlendirme ${ago(alarms.evaluatedAt)})` : '';

  return (
    <div
      className={`ov-alarm-banner ov-alarm-${tone}`}
      role="alert"
      data-testid="global-alarm-banner"
      data-tone={tone}
      data-stale={stale ? 'true' : undefined}
    >
      <span className="ov-alarm-icon" aria-hidden="true">
        <Icon.Warn size={16} />
      </span>
      <div className="ov-alarm-body">
        <div className="ov-alarm-heading">{heading}</div>
        {shown.length > 0 ? (
          <ul className="ov-alarm-list">
            {shown.map((a) => (
              <li key={a.id}>{alarmLine(a)}</li>
            ))}
            {more > 0 ? <li className="ov-alarm-more">+{more} alarm daha</li> : null}
          </ul>
        ) : null}
        {evaluatorDead ? (
          <div className="ov-alarm-evaluator" data-testid="global-alarm-evaluator-dead">
            {shown.length > 0
              ? `Alarm servisi yanıt vermiyor — gösterilen alarmlar güncel olmayabilir${lastEval}.`
              : `Yeni sorunlar fark edilmiyor ve alarm e-postası gitmiyor${lastEval}.`}
          </div>
        ) : null}
        {stale ? (
          <div className="ov-alarm-evaluator" data-testid="global-alarm-stale">
            Alarm durumu yenilenemiyor; gösterilen bilgi güncel olmayabilir
            {lastOkAt ? ` (son başarılı okuma ${ago(lastOkAt)})` : ''}.
          </div>
        ) : null}
      </div>
      {showOverviewLink ? (
        <Link to="/" className="ov-alarm-link">
          Genel Bakış'ta gör
          <Icon.ChevronRight size={12} />
        </Link>
      ) : null}
    </div>
  );
}

/**
 * V2 admin shell — header on top, sidebar+main flex below.
 * On <880px the sidebar collapses to a hamburger-triggered drawer.
 * Route changes auto-close the drawer so navigation doesn't leave it
 * hovering over the main content.
 */
export function AppShell(): JSX.Element {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const canViewHealth = useCan('provider:view_health');
  const alarms = useGlobalAlarms(canViewHealth);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const toggleSidebar = useCallback(() => setSidebarOpen((o) => !o), []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg-page)',
      }}
    >
      <Topbar
        onToggleSidebar={toggleSidebar}
        alarmCount={activeAlarmCount(alarms.data)}
        alarmCritical={alarmsCritical(alarms.data)}
        alarmStale={alarms.stale}
      />
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <Sidebar open={sidebarOpen} onClose={closeSidebar} />
        <main
          className="main"
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'auto',
          }}
        >
          <OverviewAlarmsProvider value={alarms}>
            <AlarmBanner
              alarms={alarms.data}
              stale={alarms.stale}
              lastOkAt={alarms.lastOkAt}
              showOverviewLink={location.pathname !== '/'}
            />
            <RouteErrorBoundary>
              <Outlet />
            </RouteErrorBoundary>
          </OverviewAlarmsProvider>
        </main>
      </div>
    </div>
  );
}
